import type { ReactNode } from "react";
import { formatTimestamp } from "@/lib/date-utils";

/**
 * One labeled block in the lightbox info panel. Shared by the lightbox's
 * built-in sections and the admin media page's panel extra, so both read as
 * one panel.
 */
export function LightboxPanelSection({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-zinc-400">{label}</p>
      {children}
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

/**
 * A photo's facts on one line — "1200 × 800 · 1.0 MB · Uploaded Feb 1, 2026".
 * The date is when the photo was submitted, not when it was taken, so it is
 * always labeled. Dimensions and size are left out when unknown or not passed.
 */
export function PhotoFacts({
  width,
  height,
  byteSize,
  uploadedAt,
  className,
}: {
  width: number | null;
  height: number | null;
  /** Omitted on member surfaces; admins see it alongside the filename. */
  byteSize?: number;
  uploadedAt: string;
  className?: string;
}) {
  const facts = [
    width && height ? `${width} × ${height}` : null,
    byteSize !== undefined ? formatBytes(byteSize) : null,
    `Uploaded ${formatTimestamp(uploadedAt)}`,
  ].filter((fact): fact is string => fact !== null);

  return <p className={className}>{facts.join(" · ")}</p>;
}
